/**
 * Campaign Management - Basic Information Tab
 *
 * First tab of the Campaign Management form with campaign basic settings
 */
import { type JSX } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { RadioButton } from 'primereact/radiobutton'
import {
  ZodForm,
  FormText,
  FormDropdown,
  FormNumber,
  FormTextarea,
} from '../../forms/index.js'

export const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Draft', value: 'draft' },
] as const

export const CAMPAIGN_TYPE_OPTIONS = [
  { label: 'Email Campaign', value: 'email' },
  { label: 'SMS Campaign', value: 'sms' },
  { label: 'Multi-channel Campaign', value: 'multi_channel' },
] as const

export const ROUTING_TYPE_OPTIONS = [
  { label: 'Routing Rules', value: 'routing_rules' },
  { label: 'AI Rules', value: 'ai_rules' },
] as const

export type CampaignStatus = 'active' | 'inactive' | 'draft'
export type CampaignType = 'email' | 'sms' | 'multi_channel'
export type RoutingType = 'routing_rules' | 'ai_rules'

/** Validated output emitted to the parent via `onSubmit`. */
export interface WebContactBasicInfoValues {
  status: CampaignStatus
  campaignType: CampaignType
  campaignName: string
  campaignDescription: string
  outboundEmailAccount: string
  outboundEmailFromAddress: string
  outboundEmailFromName: string
  outboundEmailReplyToAddress: string
  outboundSmsAccount: string
  ticketReopenDays: number
  olaDurationMinutes: number
  routingType: RoutingType
  routingRules?: string
  aiRoutingRules?: string
}

interface Props {
  /** Current values for an existing campaign; omit when creating. */
  defaultValues?: Partial<WebContactBasicInfoValues>
  /** Called with validated values after the user submits the tab. */
  onSubmit?: (values: WebContactBasicInfoValues) => Promise<void> | void
  /** Forwarded to the underlying <form> so a drawer can drive submission. */
  formRef?: React.RefObject<HTMLFormElement | null>
  disabled?: boolean
}

// --- Radio Button Group Component -------------------------------------------

interface RoutingTypeRadioGroupProps {
  name: string
  disabled?: boolean
}

function RoutingTypeRadioGroup({ name, disabled }: RoutingTypeRadioGroupProps): JSX.Element {
  const { register, watch } = useFormContext()
  const selectedValue = watch(name)

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">Pick One</label>
      <div className="space-y-2">
        {ROUTING_TYPE_OPTIONS.map((option) => (
          <div key={option.value} className="flex items-center gap-2">
            <RadioButton
              inputId={`routing-${option.value}`}
              value={option.value}
              {...register(name)}
              disabled={disabled}
              checked={selectedValue === option.value}
            />
            <label
              htmlFor={`routing-${option.value}`}
              className="text-sm text-gray-700 cursor-pointer"
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>
    </div>
  )
}

// --- Zod schema -----------------------------------------------------------

const WebContactBasicInfoSchema = z.object({
  status: z.enum(['active', 'inactive', 'draft']),
  campaignType: z.enum(['email', 'sms', 'multi_channel']),
  campaignName: z.string().trim().min(1, 'Campaign Name is required').max(255, 'Campaign Name is too long'),
  campaignDescription: z.string().trim().max(1000, 'Description is too long').optional(),
  outboundEmailAccount: z.string().trim().optional(),
  outboundEmailFromAddress: z.string().email('Invalid email address').optional().or(z.literal('')),
  outboundEmailFromName: z.string().trim().max(255, 'From Name is too long').optional(),
  outboundEmailReplyToAddress: z.string().email('Invalid email address').optional().or(z.literal('')),
  outboundSmsAccount: z.string().trim().optional(),
  ticketReopenDays: z
    .number({ message: 'Conversation reopen days is required' })
    .int('Conversation reopen days must be a whole number')
    .min(0, 'Conversation reopen days must be 0 or greater')
    .max(365, 'Conversation reopen days must be 365 or less'),
  olaDurationMinutes: z
    .number({ message: 'OLA duration is required' })
    .int('OLA duration must be a whole number')
    .min(1, 'OLA duration must be at least 1 minute')
    .max(10080, 'OLA duration must be 10080 minutes or less (1 week)'),
  routingType: z.enum(['routing_rules', 'ai_rules']),
  routingRules: z.string().trim().optional(),
  aiRoutingRules: z.string().trim().optional(),
}).refine(
  (data) => {
    // If routing_type is routing_rules, routing_rules should be present
    if (data.routingType === 'routing_rules') {
      return data.routingRules != null && data.routingRules.trim().length > 0
    }
    // If routing_type is ai_rules, ai_routing_rules should be present
    if (data.routingType === 'ai_rules') {
      return data.aiRoutingRules != null && data.aiRoutingRules.trim().length > 0
    }
    return true
  },
  {
    message: 'Routing rules are required based on your selection',
    path: ['routingRules'],
  },
)

export function WebContactBasicInfoTab({
  defaultValues,
  onSubmit,
  formRef,
  disabled = false,
}: Props): JSX.Element {
  const formDefaults = {
    status: defaultValues?.status ?? 'draft',
    campaignType: defaultValues?.campaignType ?? 'email',
    campaignName: defaultValues?.campaignName ?? '',
    campaignDescription: defaultValues?.campaignDescription ?? '',
    outboundEmailAccount: defaultValues?.outboundEmailAccount ?? '',
    outboundEmailFromAddress: defaultValues?.outboundEmailFromAddress ?? '',
    outboundEmailFromName: defaultValues?.outboundEmailFromName ?? '',
    outboundEmailReplyToAddress: defaultValues?.outboundEmailReplyToAddress ?? '',
    outboundSmsAccount: defaultValues?.outboundSmsAccount ?? '',
    ticketReopenDays: defaultValues?.ticketReopenDays ?? 0,
    olaDurationMinutes: defaultValues?.olaDurationMinutes ?? 60,
    routingType: defaultValues?.routingType ?? 'routing_rules',
    routingRules: defaultValues?.routingRules ?? '',
    aiRoutingRules: defaultValues?.aiRoutingRules ?? '',
  }

  return (
    <ZodForm
      schema={WebContactBasicInfoSchema}
      defaultValues={formDefaults}
      onSubmit={(data) => onSubmit?.(data as WebContactBasicInfoValues)}
      {...(formRef ? { formRef } : {})}
    >
      <div className="grid grid-cols-1 gap-6">
        {/* Status and Campaign Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormDropdown
            name="status"
            label="Status"
            required
            disabled={disabled}
            options={STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            placeholder="Select status..."
          />
          <FormDropdown
            name="campaignType"
            label="Campaign Type"
            required
            disabled={disabled}
            options={CAMPAIGN_TYPE_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            placeholder="Select campaign type..."
          />
        </div>

        {/* Campaign Name */}
        <FormText
          name="campaignName"
          label="Campaign Name"
          required
          autoFocus
          disabled={disabled}
          placeholder="Enter campaign name"
        />

        {/* Campaign Description */}
        <FormTextarea
          name="campaignDescription"
          label="Campaign Description"
          disabled={disabled}
          rows={3}
          placeholder="Enter campaign description"
        />

        {/* Outbound Email Configuration */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Outbound Email Configuration</h3>
          <div className="grid grid-cols-1 gap-4">
            <FormText
              name="outboundEmailAccount"
              label="Outbound Email Account"
              disabled={disabled}
              placeholder="Select or enter email account"
            />
            <FormText
              name="outboundEmailFromAddress"
              label="Outbound Email From Address"
              disabled={disabled}
              placeholder="e.g., noreply@company.com"
            />
            <FormText
              name="outboundEmailFromName"
              label="Outbound Email From Name"
              disabled={disabled}
              placeholder="e.g., Company Name"
            />
            <FormText
              name="outboundEmailReplyToAddress"
              label="Reply-To Email Address"
              disabled={disabled}
              placeholder="e.g., support@company.com"
            />
          </div>
        </div>

        {/* Outbound SMS Configuration */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Outbound SMS Configuration</h3>
          <FormText
            name="outboundSmsAccount"
            label="Outbound SMS Account"
            disabled={disabled}
            placeholder="Select or enter SMS account"
          />
        </div>

        {/* Conversation and OLA Configuration */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Conversation & SLA Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormNumber
              name="ticketReopenDays"
              label="Conversation Reopen Days"
              required
              min={0}
              max={365}
              disabled={disabled}
              helpText="Number of days after which a conversation can be reopened"
            />
            <FormNumber
              name="olaDurationMinutes"
              label="OLA Duration (minutes)"
              required
              min={1}
              max={10080}
              disabled={disabled}
              helpText="Operational Level Agreement duration in minutes"
            />
          </div>
        </div>

        {/* Routing Configuration */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Routing Configuration</h3>
          <RoutingTypeRadioGroup name="routingType" disabled={disabled} />

          {/* Conditional fields based on routing type */}
          <div className="mt-4">
            <FormTextarea
              name="routingRules"
              label="Routing Rules"
              disabled={disabled}
              rows={4}
              placeholder="Define routing rules..."
              helpText="Configure how contacts are routed based on your selection"
            />
            <FormTextarea
              name="aiRoutingRules"
              label="AI Routing Rules"
              disabled={disabled}
              rows={4}
              placeholder="Define AI routing rules..."
              helpText="Configure AI-powered routing logic"
            />
          </div>
        </div>
      </div>
    </ZodForm>
  )
}
